import { API_BASE_URL, fetchApi } from '@/services/api-client';

export const IT_TICKETS_PATH = '/it-tickets';

export type ItTicketStatus = 'open' | 'in_progress' | 'waiting_on_requester' | 'resolved';
export type ItTicketPriority = 'low' | 'medium' | 'high';
export type ItTicketLocation =
  | 'classroom'
  | 'library'
  | 'admin_office'
  | 'staff_office'
  | 'meeting_room'
  /** The fault is in a website or a service, which is in no room. */
  | 'not_applicable'
  | 'other';
export type ItTicketDeviceType =
  | 'laptop'
  | 'projector_smartboard'
  | 'tv'
  | 'wifi_network'
  | 'google_classroom'
  | 'student_laptop'
  | 'student_tablet'
  | 'hmh'
  | 'mcgraw_hill'
  | 'risp_platform'
  | 'other_software'
  | 'other';

export type ItTicketUser = {
  id: string;
  email: string | null;
  displayName: string;
  role: string;
  /** Relative media path — render through AdminImage, never an inline <img>. */
  photoUrl: string;
};

export type ItTicketAssignee = { id: string; displayName: string; email: string | null; photoUrl: string };

export type ItTicketEvent = {
  id: string;
  kind: 'comment' | 'status_change' | 'assignment_change';
  authorId: string;
  author: ItTicketUser | null;
  body: string | null;
  fromStatus: ItTicketStatus | null;
  toStatus: ItTicketStatus | null;
  /** Set only on an assignment_change; null on either side means unassigned. */
  fromAssigneeId: string | null;
  toAssigneeId: string | null;
  toAssignee: ItTicketUser | null;
  createdAt: string;
};

export type ItTicketAttachment = {
  id: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
};

export type ItTicketSummary = {
  id: string;
  requesterId: string;
  requester: ItTicketUser | null;
  /** The IT admin working this ticket; null means nobody has picked it up. */
  assigneeId: string | null;
  assignee: ItTicketUser | null;
  location: ItTicketLocation;
  locationOther: string | null;
  deviceTypes: ItTicketDeviceType[];
  deviceTypeOther: string | null;
  description: string;
  priority: ItTicketPriority;
  neededBy: string | null;
  status: ItTicketStatus;
  resolvedAt: string | null;
  commentCount: number;
  lastActivityAt: string;
  lastEventAuthorId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ItTicket = ItTicketSummary & { events: ItTicketEvent[]; attachments: ItTicketAttachment[] };

export type CreateItTicketData = {
  location: ItTicketLocation;
  locationOther?: string;
  deviceTypes: ItTicketDeviceType[];
  deviceTypeOther?: string;
  description: string;
  priority: ItTicketPriority;
  neededBy?: string;
};

export type ItTicketBoardFilters = { priority?: ItTicketPriority; search?: string; deviceType?: ItTicketDeviceType };

export type ItTicketArchiveFilters = {
  from?: string;
  to?: string;
  priority?: ItTicketPriority;
  location?: ItTicketLocation;
  deviceType?: ItTicketDeviceType;
  requesterId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

export type ItTicketArchivePage = { items: ItTicketSummary[]; total: number; page: number; pageSize: number };

export type ItTicketStats = {
  totals: { opened: number; resolved: number; stillOpen: number; overdue: number };
  resolutionHours: { average: number; median: number };
  byMonth: { month: string; opened: number; resolved: number }[];
  byDeviceType: { key: ItTicketDeviceType; count: number }[];
  byLocation: { key: ItTicketLocation; count: number }[];
  byPriority: { key: ItTicketPriority; count: number }[];
  topRequesters: { requesterId: string; displayName: string; count: number }[];
};

/**
 * English-only source of truth for the option sets (and their exhaustiveness via
 * `Record<...>`). Used to derive option lists / validation sets — for display, use
 * `t('enum.it_ticket_location.' + value)` etc. instead.
 */
export const IT_TICKET_LOCATION_LABELS: Record<ItTicketLocation, string> = {
  classroom: 'Classroom',
  library: 'Library',
  admin_office: 'Admin Office',
  staff_office: 'Staff office',
  meeting_room: 'Meeting Room',
  not_applicable: 'No location applicable',
  other: 'Other',
};

export const IT_TICKET_DEVICE_LABELS: Record<ItTicketDeviceType, string> = {
  laptop: 'Laptop',
  projector_smartboard: 'Projector / Smart Board',
  tv: 'TV',
  wifi_network: 'WIFI / Network',
  google_classroom: 'Google Classroom',
  student_laptop: 'Student Laptop',
  student_tablet: 'Student Tablet',
  hmh: 'HMH',
  mcgraw_hill: 'McGraw Hill',
  risp_platform: 'RISP Platform',
  other_software: 'Other software',
  other: 'Other',
};

export const IT_TICKET_PRIORITY_LABELS: Record<ItTicketPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const IT_TICKET_STATUS_LABELS: Record<ItTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_on_requester: 'Waiting on requester',
  resolved: 'Resolved',
};

function toQuery(filters: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') qs.append(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const itTicketsApi = {
  create: (data: CreateItTicketData) =>
    fetchApi<ItTicket>(IT_TICKETS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  listMine: () => fetchApi<ItTicketSummary[]>(`${IT_TICKETS_PATH}/me`),
  get: (id: string) => fetchApi<ItTicket>(`${IT_TICKETS_PATH}/${id}`),
  comment: (id: string, body: string) =>
    fetchApi<ItTicket>(`${IT_TICKETS_PATH}/${id}/events`, { method: 'POST', body: JSON.stringify({ body }) }),
  board: (filters: ItTicketBoardFilters = {}) => fetchApi<ItTicketSummary[]>(`${IT_TICKETS_PATH}${toQuery(filters)}`),
  archive: (filters: ItTicketArchiveFilters = {}) =>
    fetchApi<ItTicketArchivePage>(`${IT_TICKETS_PATH}/archive${toQuery(filters)}`),
  stats: (filters: ItTicketArchiveFilters = {}) =>
    fetchApi<ItTicketStats>(`${IT_TICKETS_PATH}/stats${toQuery(filters)}`),
  assignees: () => fetchApi<ItTicketAssignee[]>(`${IT_TICKETS_PATH}/assignees`),
  assign: (id: string, assigneeId: string | null) =>
    fetchApi<ItTicket>(`${IT_TICKETS_PATH}/${id}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeId }),
    }),
  updateStatus: (id: string, status: ItTicketStatus, comment?: string) =>
    fetchApi<ItTicket>(`${IT_TICKETS_PATH}/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, comment }),
    }),
  uploadAttachments: async (ticketId: string, files: File[]): Promise<ItTicket> => {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    const res = await fetch(`${API_BASE_URL}${IT_TICKETS_PATH}/${ticketId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return res.json() as Promise<ItTicket>;
  },
  deleteAttachment: (attachmentId: string) =>
    fetchApi<{ ok: true }>(`${IT_TICKETS_PATH}/attachments/${attachmentId}`, { method: 'DELETE' }),
};

/** The endpoint is auth-gated, so a plain <a href> would not carry the session. */
export async function downloadItTicketAttachment(attachment: ItTicketAttachment): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${IT_TICKETS_PATH}/attachments/${attachment.id}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to download attachment');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = attachment.originalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick, not inline: revoking synchronously after click can invalidate
  // the URL before the browser has started reading it, and the download then silently does
  // nothing at all.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
